/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useState} from 'react';
import User, {UserFields} from '../../../../models/userModel';
import {CommonAdminErrors, DataMode, SubscriptionStatus, UserStatus} from '../../../../util/interfaces';
import {AdminContext} from '../../../misc/adminContext';
import {
    CustomAppEvent, EventType, navigate, ResponseData, showDialog, showSnackbar, validateEmail, WithEvents
} from '@dvrd/dvr-controls';
import Account from '../../../../models/accountModel';
import {onUserEvent} from '../../../../util/events';
import UserDetails from './userDetails';
import {sendGaPageView} from '../../../../util/analytics';
import {createErrorMessage} from '../../../../util/utils';
import Subscription from '../../../../models/subscriptionModel';
import {adminGenerateVerification, generateTwoFactorReset, getAdminAccounts} from '../../../../util/requestSender';
import {ADMIN_ACCOUNT_ROUTE} from '../../../../util/constants';

const EMPTY_ERRORS: CommonAdminErrors = {
    telNumber: null,
    zipCode: null,
    address: null,
    city: null,
    email: null,
    firstname: null,
    lastname: null,
    country: null
};

export default function UserDetailsController() {
    const context = useContext(AdminContext);
    const event: CustomAppEvent = {eventName: 'onAccountEvent', handler: _onAccountEvent};
    const [user, setUser] = useState<User>(new User({}));
    const [accounts, setAccounts] = useState<Array<Account>>([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<CommonAdminErrors>(EMPTY_ERRORS);
    const [mode, setMode] = useState(DataMode.DETAILS);

    function _onAccountEvent(data: { account: Account; eventType: EventType }) {
        const {account, eventType} = data, _user = user.clone();
        if (eventType === EventType.UPDATE) setUser(_user.updateAccount(account));
        else if (eventType === EventType.DELETE) setUser(_user.removeAccount(account));
    }

    function onChange(key: keyof UserFields) {
        return function (value: string) {
            setUser(user.clone().setField(key, value));
        };
    }

    function onChangeOverride(value: boolean) {
        const _user = user.clone();
        _user.overrideLimitations = value;
        setUser(_user);
    }

    function onChangeAdminFlex(key: string) {
        return function (value: any) {
            const _user = user.clone();
            _user.setAdminFlex(key, value);
            setUser(_user);
        };
    }

    function onClose() {
        context.onChangeContext({user: null});
        history.back();
    }

    function onSubmit(_user?: User | React.MouseEvent) {
        if (!validateValues()) return;
        if (!_user || !(_user instanceof User)) _user = user;
        setLoading(true);
        _user.updateAdmin((user: User, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                setUser(user);
                setMode(DataMode.DETAILS);
                showSnackbar('De gebruiker is bijgewerkt');
            } else
                showDialog(createErrorMessage(data.message ?? 'Het bijwerken van de gebruiker is niet gelukt.'),
                    'Bijwerken mislukt');
        });
    }

    function onClickAccount(account: Account) {
        return function () {
            context.onChangeContext({account}, () => {
                navigate(ADMIN_ACCOUNT_ROUTE);
            });
        };
    }

    function onClickDelete() {
        showDialog('Weet je zeker dat je deze gebruiker wilt verwijderen?', 'Bevestig',
            ['Nee', {label: 'Ja', onClick: onConfirmDelete, primary: false}]);
    }

    function onConfirmDelete() {
        setLoading(true);
        user.deleteAdmin((user: User, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (data.success) {
                onUserEvent(user, EventType.DELETE);
                showSnackbar('De gebruiker is verwijderd');
                history.back();
            } else
                showDialog(createErrorMessage(data.message ?? 'Het verwijderen van de gebruiker is niet gelukt.'),
                    'Verwijderen mislukt');
        });
    }

    function onClickLock() {
        const message = user.status === UserStatus.ACTIVE ? 'blokkeren' : 'deblokkeren';
        showDialog(`Weet je zeker dat je deze gebruiker wilt ${message}?`, 'Bevestig',
            [{label: 'Nee', primary: false}, {label: 'Ja', onClick: onConfirmLock}]);
    }

    function onConfirmLock() {
        const _user = user.clone();
        if (_user.status === UserStatus.ACTIVE) _user.status = UserStatus.INACTIVE;
        else _user.status = UserStatus.ACTIVE;
        setUser(_user);
        onSubmit(_user);
    }

    function onResetError(key: keyof CommonAdminErrors) {
        return function () {
            setErrors(Object.assign({}, errors, {[key]: null}));
        };
    }

    function onChangeMode(mode: DataMode) {
        return function () {
            setMode(mode);
        };
    }

    function onChangeSubscriptionStatus(status: SubscriptionStatus) {
        const _user = user.clone();
        if (!_user.subscription) _user.subscription = new Subscription();
        _user.subscription.status = status;
        setUser(_user);
    }

    function onResetTwoFactor() {
        showDialog('Weet je zeker dat de tweestapsverificatie voor deze gebruiker gereset moet worden?', 'Bevestig',
            ['Nee', {label: 'Ja', primary: false, onClick: onConfirmResetTwoFactor}]);
    }

    function onConfirmResetTwoFactor() {
        setLoading(true);
        generateTwoFactorReset({
            data: {user_id: user.id}, callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) showSnackbar('De mail is verzonden');
                else
                    showDialog(data.message ?? 'Het genereren van de token is niet gelukt.', 'Genereren mislukt');
            }
        });
    }

    function onGenerateVerification() {
        setLoading(true);
        adminGenerateVerification({
            data: {user_id: user.id}, callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success)
                    showDialog(`De gebruiker kan inloggen met de code ${data.token}.`, 'Code gegenereerd');
                else showDialog(data.message ?? 'Het genereren van de token is niet gelukt.', 'Genereren mislukt');
            }
        });
    }

    function validateValues(): boolean {
        const _errors = Object.assign({}, errors);
        let valid: boolean = true;
        if (!user.firstname) {
            valid = false;
            errors.firstname = 'De voornaam mag niet leeg zijn';
        }
        if (!user.lastname) {
            valid = false;
            errors.lastname = 'De achternaam mag niet leeg zijn';
        }
        if (!user.email) {
            valid = false;
            errors.email = 'Het e-mailadres mag niet leeg zijn';
        } else if (!validateEmail(user.email)) {
            valid = false;
            errors.email = 'Het e-mailadres is niet geldig';
        }
        if (!user.address) {
            valid = false;
            errors.address = 'Het adres mag niet leeg zijn';
        }
        if (!user.zipCode) {
            valid = false;
            errors.zipCode = 'De postcode mag niet leeg zijn';
        }
        if (!user.city) {
            valid = false;
            errors.city = 'De stad mag niet leeg zijn';
        }
        if (!user.country) {
            valid = false;
            errors.country = 'Het land mag niet leeg zijn';
        }
        if (!user.telNumber) {
            valid = false;
            errors.telNumber = 'Het telefoonnummer mag niet leeg zijn';
        }
        if (!valid) setErrors(_errors);
        return valid;
    }

    function loadAccounts() {
        const user = context.activeUser;
        if (!user) return;
        setLoading(true);
        getAdminAccounts({
            id: user.id, callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success)
                    setAccounts(data.accounts.map((acc: any) => new Account({...acc, user_id: user?.id})));
                else
                    showDialog(data.message ?? 'Het laden van de accounts is niet gelukt.', 'Account niet gevonden');
            }
        });
    }

    useEffect(() => {
        sendGaPageView('/admin/gebruiker-details');
        const user = context.activeUser;
        if (!user) return history.back();
        else {
            setUser(user);
            loadAccounts();
        }
    }, []);

    return (
        <WithEvents events={event}>
            <UserDetails onChange={onChange} onClose={onClose} onSubmit={onSubmit} onClickAccount={onClickAccount}
                         onClickDelete={onClickDelete} onClickLock={onClickLock} onResetError={onResetError}
                         onChangeMode={onChangeMode} onChangeSubscriptionStatus={onChangeSubscriptionStatus}
                         onResetTwoFactor={onResetTwoFactor} onChangeOverride={onChangeOverride}
                         onGenerateVerification={onGenerateVerification} onChangeAdminFlex={onChangeAdminFlex}
                         errors={errors} mode={mode} user={user} loading={loading} accounts={accounts}/>
        </WithEvents>
    );
}